import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import CheckIcon from "@mui/icons-material/Check";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PersonOffOutlinedIcon from "@mui/icons-material/PersonOffOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import classNames from "classnames";
import { useSelector } from "react-redux";
import { selectSelectedTakeActors } from "../../../app/slices/TrainingSlice";
import ActorAvatar from "../../../components/actors/ActorAvatar";

const NO_ACTOR_VALUE = "__no_actor__";

export default function ConversationLookAtDropdown(props: {
  selectedTargetActorId: string;
  onChange: (actorId: string) => void;
  disabled?: boolean;
  variant: "input" | "inline";
}) {
  const { selectedTargetActorId, onChange, disabled = false, variant } = props;
  const actors = useSelector(selectSelectedTakeActors) ?? [];
  const selectedActor = actors.find(
    (actor) => actor.id === selectedTargetActorId
  );
  const selectedLabel = selectedActor?.workingName ?? "No actor";
  const selectedValue = selectedActor?.id ?? NO_ACTOR_VALUE;
  const isInput = variant === "input";

  return (
    <div
      className={classNames("min-w-0", {
        "w-full": isInput,
        "flex w-auto max-w-[180px] items-center": !isInput,
      })}
      onPointerDown={(event) => event.stopPropagation()}
      onClick={(event) => event.stopPropagation()}
      data-testid={`conversation-look-at-${variant}`}
    >
      <DropdownMenu.Root>
        <DropdownMenu.Trigger
          type="button"
          disabled={disabled}
          aria-label={`Look at: ${selectedLabel}`}
          title={`Look at: ${selectedLabel}`}
          className={classNames(
            "min-w-0 appearance-none border-0 text-interaction-primary focus:outline-none",
            {
              "flex h-[38px] w-full items-center rounded-full border-[1.5px] border-solid border-interaction-primary bg-creator-surface px-[8px] text-[14px] focus-visible:ring-2 focus-visible:ring-interaction-primary":
                isInput,
              "inline-flex h-5 max-w-full items-center bg-transparent p-0 text-left text-[13px] font-medium leading-5 underline":
                !isInput,
              "cursor-pointer hover:bg-interaction-primary-washed":
                isInput && !disabled,
              "cursor-pointer hover:text-interaction-secondary":
                !isInput && !disabled,
              "cursor-not-allowed opacity-50": disabled,
            }
          )}
        >
          {isInput ? (
            <>
              <VisibilityOutlinedIcon
                aria-hidden="true"
                fontSize="inherit"
                className="shrink-0 text-[20px]"
              />
              {selectedActor && (
                <span className="ml-[6px] shrink-0">
                  <ActorAvatar actor={selectedActor} size="24" />
                </span>
              )}
              {!selectedActor && (
                <PersonOffOutlinedIcon
                  aria-hidden="true"
                  fontSize="inherit"
                  className="ml-[6px] shrink-0 text-[18px]"
                />
              )}
              <span className="ml-[6px] min-w-0 flex-1 truncate text-left font-medium">
                {selectedLabel}
              </span>
              <ExpandMoreIcon
                aria-hidden="true"
                fontSize="inherit"
                className="ml-[4px] shrink-0 text-[20px]"
              />
            </>
          ) : (
            <>
              <VisibilityOutlinedIcon
                aria-hidden="true"
                fontSize="inherit"
                className="mr-[4px] shrink-0 text-[16px]"
              />
              <span className="block truncate">{selectedLabel}</span>
            </>
          )}
        </DropdownMenu.Trigger>

        <DropdownMenu.Portal>
          <DropdownMenu.Content
            side={isInput ? "top" : "bottom"}
            sideOffset={6}
            align="start"
            collisionPadding={8}
            className="z-40 flex max-h-[200px] min-w-[160px] flex-col gap-[4px] overflow-y-auto rounded-[5px] border border-solid border-creator-border bg-creator-surface py-[8px] shadow-md focus:outline-none pit-scrollbar"
            style={{ minWidth: "var(--radix-dropdown-menu-trigger-width)" }}
          >
            <DropdownMenu.RadioGroup
              value={selectedValue}
              onValueChange={(value) =>
                onChange(value === NO_ACTOR_VALUE ? "" : value)
              }
            >
              <LookAtItem
                value={NO_ACTOR_VALUE}
                selected={selectedValue === NO_ACTOR_VALUE}
                icon={
                  <PersonOffOutlinedIcon
                    aria-hidden="true"
                    fontSize="inherit"
                    className="text-[20px]"
                  />
                }
                label="No actor"
              />
              {actors.map((actor) => (
                <LookAtItem
                  key={actor.id}
                  value={actor.id}
                  selected={selectedValue === actor.id}
                  icon={<ActorAvatar actor={actor} size="24" />}
                  label={actor.workingName}
                />
              ))}
            </DropdownMenu.RadioGroup>
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
    </div>
  );
}

function LookAtItem(props: {
  value: string;
  selected: boolean;
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <DropdownMenu.RadioItem
      value={props.value}
      className={classNames(
        "mx-[6px] flex h-[36px] cursor-pointer items-center gap-[8px] rounded-[5px] px-[8px] text-[14px] font-medium text-interaction-text-primary outline-none hover:bg-creator-primary-washed focus:bg-creator-primary-washed",
        {
          "bg-interaction-text-selectedBackground": props.selected,
        }
      )}
    >
      <span className="flex h-[24px] w-[24px] shrink-0 items-center justify-center text-interaction-primary">
        {props.icon}
      </span>
      <span className="min-w-0 flex-1 truncate">{props.label}</span>
      <span className="flex h-[18px] w-[18px] shrink-0 items-center justify-center text-interaction-primary">
        {props.selected && <CheckIcon aria-hidden="true" fontSize="inherit" />}
      </span>
    </DropdownMenu.RadioItem>
  );
}
